import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { embedStorage } from '../services/embedStorage';
import { getAllBrandPresets } from '../config/brandPresets';

const { FiAlertCircle, FiLoader } = FiIcons;

function EmbedViewer() {
  const { slug } = useParams();
  const [embed, setEmbed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [iframeHeight, setIframeHeight] = useState(700);
  const [brandPresets, setBrandPresets] = useState({});
  const iframeRef = useRef(null);

  useEffect(() => {
    loadBrandPresets();
  }, []);

  useEffect(() => {
    const fetchEmbed = async () => {
      try {
        setLoading(true);
        const data = await embedStorage.getEmbedBySlug(slug);
        if (data) {
          setEmbed(data);
          embedStorage.trackView(data.id);
        } else {
          setError('Embed not found');
        }
      } catch (err) {
        setError('Connection error');
      } finally {
        setLoading(false);
      }
    };

    fetchEmbed();
  }, [slug]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'resize' && event.data.height) {
        setIframeHeight(event.data.height);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const loadBrandPresets = async () => {
    const presets = await getAllBrandPresets();
    setBrandPresets(presets);
  };

  // Set tab title and favicon based on brand
  useEffect(() => {
    if (!embed || !Object.keys(brandPresets).length) return;
    const brand = brandPresets[embed.brand] || brandPresets.wwsc || {};

    document.title = embed.layout?.ogTitle || embed.title || brand.name || 'Tools';

    if (brand.logoUrl) {
      let link = document.querySelector("link[rel*='icon']");
      if (link) {
        link.href = brand.logoUrl;
      }
    }
  }, [embed, brandPresets]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <SafeIcon icon={FiLoader} className="text-4xl text-blue-500 animate-spin" />
      </div>
    );
  }

  if (error || !embed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="text-center max-w-md">
          <SafeIcon icon={FiAlertCircle} className="text-6xl text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Oops! Link Expired</h2>
          <p className="text-gray-600 mb-6">This branded tool link is either incorrect or has been deactivated.</p>
          <a href="/" className="inline-block bg-blue-600 text-white px-8 py-3 rounded-2xl font-bold shadow-lg shadow-blue-100">
            Back to Home
          </a>
        </div>
      </div>
    );
  }

  const brand = brandPresets[embed.brand] || brandPresets.wwsc || {};

  const calculateHeight = () => {
    // Priority 1: If using custom-manual preset, use the exact custom height
    if (embed.iframe.dimensionPreset === 'custom-manual' && embed.iframe.customHeight) {
      return `${embed.iframe.customHeight}px`;
    }

    // Priority 2: If heightMode is explicitly set to fixed, use fixedHeight
    if (embed.iframe.heightMode === 'fixed' && embed.iframe.fixedHeight) {
      return `${embed.iframe.fixedHeight}px`;
    }

    // Priority 3: If heightMode is viewport, calculate based on viewport
    if (embed.iframe.heightMode === 'viewport') {
      return 'calc(100vh - 120px)';
    }

    // Priority 4: Default responsive behavior with postMessage or minHeight
    return `${Math.max(iframeHeight, embed.iframe.minHeight || 700)}px`;
  };

  const calculateWidth = () => {
    // If using custom-manual preset, use the exact custom width
    if (embed.iframe.dimensionPreset === 'custom-manual' && embed.iframe.customWidth) {
      return `${embed.iframe.customWidth}px`;
    }

    // Default to 100% for responsive behavior
    return '100%';
  };

  return (
    <div className="min-h-screen transition-colors duration-700" style={{ backgroundColor: embed.layout.backgroundColor }}>
      <div
        className="mx-auto flex flex-col min-h-screen"
        style={{
          maxWidth: `${embed.layout.maxWidth}px`,
          padding: `${embed.layout.padding}px`
        }}
      >
        {/* The Branded Wrapper Header */}
        <AnimatePresence>
          {embed.layout.showHeader && (
            <motion.header
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex items-center justify-between px-6 py-4 mb-4 ${brand.layout?.shadow || 'shadow-lg'}`}
              style={{
                background:
                  embed.layout.headerStyle === 'gradient'
                    ? `linear-gradient(to right, ${brand.primaryColor}, ${brand.secondaryColor})`
                    : embed.layout.headerStyle === 'minimal'
                    ? 'transparent'
                    : brand.primaryColor,
                color: embed.layout.headerStyle === 'minimal' ? '#1f2937' : brand.textColor,
                borderRadius: `${embed.layout.borderRadius}px`
              }}
            >
              <div className="flex items-center gap-4">
                {brand.logoUrl && <img src={brand.logoUrl} alt="Logo" className="h-8 md:h-10 w-auto object-contain" />}
                <h1 className="text-lg md:text-xl font-extrabold tracking-tight">
                  {embed.layout.headerText || embed.title}
                </h1>
              </div>
            </motion.header>
          )}
        </AnimatePresence>

        {/* The iframe Tool Container */}
        <motion.main
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className={`flex-1 bg-white overflow-hidden relative ${brand.layout?.shadow || 'shadow-lg'}`}
          style={{
            borderRadius: `${embed.layout.borderRadius}px`,
            minHeight: embed.iframe.minHeight || 700,
            maxWidth: calculateWidth()
          }}
        >
          <iframe
            ref={iframeRef}
            src={embed.toolUrl}
            className="w-full h-full border-0"
            style={{
              height: calculateHeight(),
              width: calculateWidth(),
              borderRadius: `${embed.layout.borderRadius}px`
            }}
            allow={embed.iframe.allow}
            sandbox={embed.iframe.sandbox}
            title={embed.title}
          />
        </motion.main>
      </div>
    </div>
  );
}

export default EmbedViewer;